import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, ArrowLeft } from 'lucide-react'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { storeDigitalCoworkerOAuthState } from '@/components/DigitalCoworkerGoogleCallback'
import { storeDigitalCoworkerOneDriveOAuthState } from '@/components/DigitalCoworkerOneDriveCallback'
import { apiService } from '@/service/api'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import WizardLayout from './WizardLayout'
import AddKnowledgeModal from './AddKnowledgeModal'

const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/calendar',
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/drive.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
].join(' ')

const MS_SCOPES = 'User.Read Files.Read.All offline_access'

export default function ConnectAccountsV2() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const coworkerId = location.state?.coworkerId as string | undefined
  const formData = location.state?.formData ?? {}

  const [isGoogleConnected, setIsGoogleConnected] = useState(
    location.state?.googleConnected === true,
  )
  const [googleEmail, setGoogleEmail] = useState<string | null>(
    location.state?.googleEmail ?? null,
  )
  const [isOneDriveConnected, setIsOneDriveConnected] = useState(
    location.state?.oneDriveConnected === true,
  )
  const [oneDriveEmail, setOneDriveEmail] = useState<string | null>(
    location.state?.oneDriveEmail ?? null,
  )
  const [isOneDriveDisconnecting, setIsOneDriveDisconnecting] = useState(false)
  const [knowledgeModalOpen, setKnowledgeModalOpen] = useState(false)
  const [knowledgeDocIds, setKnowledgeDocIds] = useState<string[]>([])

  useEffect(() => {
    if (location.state?.googleConnected) {
      setIsGoogleConnected(true)
      setGoogleEmail(location.state.googleEmail ?? null)
    }
    if (location.state?.oneDriveConnected) {
      setIsOneDriveConnected(true)
      setOneDriveEmail(location.state.oneDriveEmail ?? null)
    }
  }, [location.state])

  const handleConnectGoogle = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_client_id'))
      return
    }
    if (!coworkerId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_coworker_id'))
      navigate('/digital-coworker/new', { state: { formData } })
      return
    }
    const state = crypto.randomUUID()
    storeDigitalCoworkerOAuthState(coworkerId, state, formData)
    const redirectUri = `${window.location.origin}/auth/callback/digital-coworker-google`
    window.location.href =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?response_type=code&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(GOOGLE_SCOPES)}` +
      `&state=${encodeURIComponent(state)}&access_type=offline&prompt=consent`
  }

  const handleConnectOneDrive = () => {
    const clientId = import.meta.env.VITE_MS_TEAMS_CLIENT_ID
    if (!clientId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_onedrive_client_id'))
      return
    }
    if (!coworkerId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_coworker_id'))
      navigate('/digital-coworker/new', { state: { formData } })
      return
    }

    const state = crypto.randomUUID()
    const formDataWithConnections = {
      ...formData,
      googleConnected: isGoogleConnected,
      googleEmail: googleEmail ?? undefined,
    }
    storeDigitalCoworkerOneDriveOAuthState(coworkerId, state, formDataWithConnections)

    const redirectUri = `${window.location.origin}/auth/callback/digital-coworker-onedrive`
    window.location.href =
      `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize` +
      `?response_type=code` +
      `&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(MS_SCOPES)}` +
      `&state=${encodeURIComponent(state)}` +
      `&prompt=consent`
  }

  const handleDisconnectOneDrive = async () => {
    if (!coworkerId) {
      toast.error(t('digital_coworker:connect_accounts.errors.missing_coworker_id'))
      return
    }
    setIsOneDriveDisconnecting(true)
    try {
      await apiService.unlinkDigitalCoworkerMicrosoftAccount(coworkerId)
      setIsOneDriveConnected(false)
      setOneDriveEmail(null)
    } catch {
      toast.error(t('digital_coworker:connect_accounts.errors.disconnect_failed', 'Failed to disconnect. Please try again.'))
    } finally {
      setIsOneDriveDisconnecting(false)
    }
  }

  const handleContinue = () => {
    navigate('/digital-coworker/setup-interviews', {
      state: {
        formData: {
          ...formData,
          googleConnected: isGoogleConnected,
          googleEmail: googleEmail ?? undefined,
          oneDriveConnected: isOneDriveConnected,
          oneDriveEmail: oneDriveEmail ?? undefined,
        },
        coworkerId,
      },
    })
  }

  const handleBack = () => {
    navigate('/digital-coworker/new', { state: { formData, coworkerId } })
  }

  const connections: Array<{
    id: string
    name: string
    description: string
    icon: JSX.Element
    connected: boolean
    connectedLabel?: string
    onConnect?: () => void
    onDisconnect?: () => void
    disconnecting?: boolean
    disabled?: boolean
    isUpload?: boolean
  }> = [
    {
      id: 'google',
      name: t('digital_coworker:connect_accounts.google.title', 'Google'),
      description: t('digital_coworker:connect_accounts.google.description', 'Connect your Google account'),
      icon: (
        <svg viewBox="0 0 24 24" className="size-8">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      ),
      connected: isGoogleConnected,
      connectedLabel: googleEmail
        ? t('digital_coworker:connect_accounts.google.connected_as', { email: googleEmail })
        : undefined,
      onConnect: handleConnectGoogle,
    },
    {
      id: 'microsoft',
      name: t('digital_coworker:connect_accounts.microsoft', 'Microsoft'),
      description: 'Connect your Microsoft',
      icon: (
        <svg viewBox="0 0 24 24" className="size-8">
          <rect x="1" y="1" width="10" height="10" fill="#F25022" />
          <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
          <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
          <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
        </svg>
      ),
      connected: isOneDriveConnected,
      connectedLabel: oneDriveEmail
        ? t('digital_coworker:connect_accounts.onedrive.connected_as', { email: oneDriveEmail })
        : undefined,
      onConnect: handleConnectOneDrive,
      onDisconnect: handleDisconnectOneDrive,
      disconnecting: isOneDriveDisconnecting,
    },
    {
      id: 'box',
      name: 'Box',
      description: 'Connect your Box Account',
      icon: (
        <svg viewBox="0 0 48 48" className="size-8">
          <path fill="#0061D5" d="M8.4 18.2L17.6 24l-9.2 5.8V18.2z" />
          <path fill="#0061D5" d="M39.6 29.8L30.4 24l9.2-5.8v11.6z" />
          <path fill="#0061D5" d="M17.6 24l6.4 4.2 6.4-4.2-6.4-4.2-6.4 4.2z" />
          <path fill="#0061D5" d="M8.4 30.4l9.2 5.8 6.4-4.2-9.2-5.8-6.4 4.2z" />
          <path fill="#0061D5" d="M39.6 30.4l-9.2 5.8-6.4-4.2 9.2-5.8 6.4 4.2z" />
          <path fill="#0061D5" d="M8.4 18.2l9.2-5.8 6.4 4.2-9.2 5.8-6.4-4.2z" />
          <path fill="#0061D5" d="M39.6 18.2l-9.2-5.8-6.4 4.2 9.2 5.8 6.4-4.2z" />
        </svg>
      ),
      connected: false,
      disabled: true,
    },
    {
      id: 'upload',
      name: t('digital_coworker:connect_accounts.upload_data.title', 'Upload Data'),
      description: knowledgeDocIds.length > 0
        ? t('digital_coworker:connect_accounts.upload_data.added', {
            defaultValue: '{{count}} document(s) added',
            count: knowledgeDocIds.length,
          })
        : t('digital_coworker:connect_accounts.upload_data.description', 'Add files like SOPs, manuals, or guides (pdf, docx, xlsx)'),
      icon: (
        <svg className="size-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.58 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.58 4 8 4s8-1.79 8-4M4 7c0-2.21 3.58-4 8-4s8 1.79 8 4" />
        </svg>
      ),
      connected: false,
      disabled: false,
      isUpload: true,
      onConnect: () => setKnowledgeModalOpen(true),
    },
  ]

  return (
    <WizardLayout step={2}>
      <h2 className="text-lg font-semibold mb-6">
        {t('digital_coworker:connect_accounts.title', 'Connect your accounts')}
      </h2>

      <div className="space-y-3">
        {connections.map((c) => (
          <div
            key={c.id}
            className="flex items-center justify-between rounded-lg border border-[#e5e5e5] p-4 dark:border-dark-border"
          >
            <div className="flex items-center gap-4">
              <div className="flex size-10 items-center justify-center">{c.icon}</div>
              <div>
                <h3 className="text-sm font-medium">{c.name}</h3>
                <p className="text-xs text-muted-foreground">
                  {c.connected && c.connectedLabel ? c.connectedLabel : c.description}
                </p>
              </div>
            </div>
            {c.connected ? (
              <IntuiButton
                variant="outline"
                size="sm"
                className="text-destructive border-destructive hover:bg-destructive/10"
                onClick={c.onDisconnect}
                disabled={!c.onDisconnect || c.disconnecting}
              >
                {t('digital_coworker:connect_accounts.disconnect', 'Disconnect')}
              </IntuiButton>
            ) : (
              <IntuiButton
                variant="outline"
                size="sm"
                onClick={c.onConnect}
                disabled={c.disabled}
              >
                {c.isUpload
                  ? t('digital_coworker:connect_accounts.upload_data.button', 'Upload')
                  : t('digital_coworker:connect_accounts.google.connect_button', 'Connect')}
              </IntuiButton>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-8">
        <IntuiButton variant="outline" leftIcon={<ArrowLeft />} onClick={handleBack}>
          {t('digital_coworker:buttons.back', 'Back')}
        </IntuiButton>
        <IntuiButton
          rightIcon={<ArrowRight />}
          onClick={handleContinue}
        >
          {t('digital_coworker:buttons.continue', 'Continue')}
        </IntuiButton>
      </div>

      <AddKnowledgeModal
        isOpen={knowledgeModalOpen}
        onClose={() => setKnowledgeModalOpen(false)}
        coworkerId={coworkerId}
        onSubmitted={(ids) => setKnowledgeDocIds((prev) => [...new Set([...prev, ...ids])])}
      />
    </WizardLayout>
  )
}
